import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { Label } from "@shared/components/ui/label";
import {
  useArchiveExhibition,
  useCreateExhibition,
  useDeleteExhibition,
  useExhibition,
  usePurgeExhibition,
  useRegenerateExhibitionCode,
  useReorderZonesInExhibition,
  useRestoreExhibition,
  useTransitionExhibition,
  useUpdateExhibition,
  useUploadExhibitionImage,
  useZonesInExhibition,
} from "@shared/api/hooks/useExhibitions";
import { exportExhibition } from "@shared/api/exhibitions";
import BatchUploadDialog from "@staff/features/exhibitions/BatchUploadDialog";
import ImageUpload from "@staff/features/shared/ImageUpload";
import LockdownBanner from "@staff/features/shared/LockdownBanner";
import LocalizedContentTabs from "@shared/components/LocalizedContentTabs";
import CodeSection from "@shared/components/CodeSection";
import type { ContentLanguage } from "@shared/lib/language";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API = `${BACKEND_URL}/api`;
import { useDepartments, useTeams } from "@shared/api/hooks/useOrg";
import { ApiError } from "@shared/api/client";
import { ArrowDown, ArrowLeft, ArrowUp } from "lucide-react";
import { toast } from "sonner";

// F-B — Exhibition create / edit form.
// Editor self-lockout: an editor patching owner fields to a value they
// don't match returns 422; the form surfaces the backend error message.

interface FormState {
  titleTc: string;
  titleEn: string;
  titleSc: string;
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
  ownerTeamId: string;
  ownerDepartmentId: string;
}

const EMPTY: FormState = {
  titleTc: "",
  titleEn: "",
  titleSc: "",
  descriptionTc: "",
  descriptionEn: "",
  descriptionSc: "",
  ownerTeamId: "",
  ownerDepartmentId: "",
};

// The exhibition wire shape is camelCase (ADR-0020), so map the shared
// LocalizedContentTabs language key to this form's field name.
const DESC_FIELD: Record<ContentLanguage, keyof FormState> = {
  tc: "descriptionTc",
  en: "descriptionEn",
  sc: "descriptionSc",
};

const TITLE_FIELD: Record<ContentLanguage, keyof FormState> = {
  tc: "titleTc",
  en: "titleEn",
  sc: "titleSc",
};

const ExhibitionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { data: existing, isLoading: loadingExisting } = useExhibition(id);
  const { data: teams = [] } = useTeams();
  const { data: departments = [] } = useDepartments();
  // Non-admins are offered only owners covering THEMSELVES — exactly what
  // the backend scope-lockout guard accepts (anything else 422s at save).
  const {
    isAdmin,
    teamIds: myTeamIds = [],
    departmentId: myDepartmentId = null,
  } = useAuth();
  const scopedTeams = isAdmin
    ? teams
    : teams.filter((t) => myTeamIds.includes(t.id));
  const scopedDepartments = isAdmin
    ? departments
    : departments.filter((d) => d.id === myDepartmentId);
  // F-C: dedicated zones-in-exhibition fetch (returns ALL zones for
  // this exhibition regardless of state — including archived + purged).
  // Replaced the previous useZoneList + client-filter approach which
  // could come back empty when the role-aware /zones listing excluded
  // the cascade-archived parent's children for editors.
  const { data: zonesInExhibition = [] } = useZonesInExhibition(
    isEdit ? id : undefined,
  );
  const reorderZones = useReorderZonesInExhibition();

  // Zone ordering (2026-09-07): swap with the neighbour in the current list
  // and PUT the full sequence. Saves immediately — this table isn't form
  // state, so a pending order would otherwise be invisible to the Save button.
  const moveZone = (zoneId: string, direction: -1 | 1) => {
    if (!id) return;
    const ids = zonesInExhibition.map((z) => z.id);
    const pos = ids.indexOf(zoneId);
    const target = pos + direction;
    if (pos < 0 || target < 0 || target >= ids.length) return;
    [ids[pos], ids[target]] = [ids[target], ids[pos]];
    reorderZones.mutate(
      { exhibitionId: id, zoneIds: ids },
      {
        onError: (err: unknown) => {
          toast.error(
            err instanceof ApiError && err.message
              ? err.message
              : "Failed to reorder zones",
          );
        },
      },
    );
  };

  const createMut = useCreateExhibition();
  const updateMut = useUpdateExhibition();
  const deleteMut = useDeleteExhibition();
  const transitionMut = useTransitionExhibition();
  const regenCodeMut = useRegenerateExhibitionCode();
  const archiveMut = useArchiveExhibition();
  const restoreMut = useRestoreExhibition();
  const purgeMut = usePurgeExhibition();
  const uploadImageMut = useUploadExhibitionImage();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  // Staged cover removal (2026-09-08): applied by Save via `removeCoverImage`.
  const [removeCover, setRemoveCover] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  // F-C lockdown: archived / purged exhibitions are read-only.
  // Restore is the only allowed mutation; everything else is disabled.
  const isArchived = existing?.state === "archived";
  const isPurged = existing?.state === "purged";
  const isLocked = isArchived || isPurged;

  // Create-mode preselect for non-admins: start the form on an owner that
  // covers the actor (their department, else their first team) so a fresh
  // create is valid without hunting through the selects.
  const [form, setForm] = useState<FormState>(() => {
    if (isEdit || isAdmin) return EMPTY;
    return {
      ...EMPTY,
      ownerDepartmentId: myDepartmentId ?? "",
      ownerTeamId: myDepartmentId ? "" : (myTeamIds[0] ?? ""),
    };
  });

  useEffect(() => {
    if (!existing) return;
    setForm({
      titleTc: existing.titleTc ?? "",
      titleEn: existing.titleEn ?? "",
      titleSc: existing.titleSc ?? "",
      descriptionTc: existing.descriptionTc ?? "",
      descriptionEn: existing.descriptionEn ?? "",
      descriptionSc: existing.descriptionSc ?? "",
      ownerTeamId: existing.ownerTeamId ?? "",
      ownerDepartmentId: existing.ownerDepartmentId ?? "",
    });
    if (existing.coverImageId) {
      setImagePreview(`${API}/assets/${existing.coverImageId}`);
    }
  }, [existing]);

  const handleFileSelected = (file: File) => {
    setRemoveCover(false); // a new pick supersedes a staged removal
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };
  const handleFileTooLarge = () => toast.error("Image must be less than 10MB");

  const setOwnerTeam = (e: ChangeEvent<HTMLSelectElement>) =>
    setForm((f) => ({ ...f, ownerTeamId: e.target.value }));
  const setOwnerDept = (e: ChangeEvent<HTMLSelectElement>) =>
    setForm((f) => ({ ...f, ownerDepartmentId: e.target.value }));

  // Option (a) parity with zone/exhibit forms (2026-07-27): Save is
  // disabled in edit mode until something differs from the saved record.
  // Bespoke check (this form doesn't use useEntityForm): every FormState
  // key vs `existing`, plus a newly picked cover image.
  const textDirty =
    isEdit && existing
      ? (Object.keys(form) as (keyof FormState)[]).some(
          (k) => form[k] !== ((existing[k] as string | null) ?? ""),
        )
      : true;
  const dirty = textDirty || imageFile !== null || removeCover;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload = {
      titleTc: form.titleTc,
      descriptionTc: form.descriptionTc,
      titleEn: form.titleEn || undefined,
      titleSc: form.titleSc || undefined,
      descriptionEn: form.descriptionEn || undefined,
      descriptionSc: form.descriptionSc || undefined,
      ownerTeamId: form.ownerTeamId || null,
      ownerDepartmentId: form.ownerDepartmentId || null,
      ...(removeCover ? { removeCoverImage: true } : {}),
    };
    const onError = (err: unknown) => {
      const message =
        err instanceof ApiError && err.message ? err.message : "Save failed";
      toast.error(message);
    };
    const uploadIfNeeded = async (targetId: string) => {
      if (!imageFile) return;
      try {
        await uploadImageMut.mutateAsync({
          exhibitionId: targetId,
          file: imageFile,
        });
        setImageFile(null);
      } catch (err) {
        onError(err);
      }
    };
    if (id) {
      // Skip the no-op text PATCH when only the cover image changed —
      // parity with the zone/exhibit forms (2026-07-27): it would write
      // an exhibition:update audit row with an empty diff.
      if (!textDirty && !removeCover) {
        void uploadIfNeeded(id).then(() => toast.success("Exhibition saved"));
        return;
      }
      updateMut.mutate(
        { exhibitionId: id, payload },
        {
          onSuccess: async () => {
            if (removeCover) {
              setRemoveCover(false);
              setImagePreview(null);
            }
            await uploadIfNeeded(id);
            toast.success("Exhibition saved");
          },
          onError,
        },
      );
    } else {
      createMut.mutate(payload, {
        onSuccess: async (ex) => {
          await uploadIfNeeded(ex.id);
          toast.success("Exhibition created");
          navigate(`/staff/exhibition/${ex.id}/edit`);
        },
        onError,
      });
    }
  };

  const handlePublish = () => {
    if (!existing || !id) return;
    const next = existing.state === "published" ? "draft" : "published";
    transitionMut.mutate(
      { exhibitionId: id, toState: next },
      {
        onSuccess: () =>
          toast.success(
            next === "published"
              ? "Exhibition published"
              : "Exhibition unpublished",
          ),
        onError: (err) =>
          toast.error(
            err instanceof ApiError && err.message
              ? err.message
              : "Failed to update lifecycle",
          ),
      },
    );
  };

  const handleRegenCode = () => {
    if (!id) return;
    regenCodeMut.mutate(id, {
      onSuccess: (out) => toast.success(`New code: ${out.code}`),
      onError: () => toast.error("Failed to regenerate code"),
    });
  };

  const handleArchive = () => {
    if (!id || !existing) return;
    const publishedZones = zonesInExhibition.filter(
      (z) => z.state === "published",
    ).length;
    const totalExhibits = zonesInExhibition.reduce(
      (acc, z) => acc + (z.exhibitIds?.length ?? 0),
      0,
    );
    const ok = window.confirm(
      [
        `Archive ${existing.titleTc || existing.code}?`,
        "",
        "This will archive:",
        "  • the exhibition",
        `  • ${zonesInExhibition.length} zones (${publishedZones} currently published)`,
        `  • ${totalExhibits} exhibits inside those zones`,
        "",
        "Draft content stays as-is. Visitors will no longer see this",
        "exhibition or its children. When you restore later, each piece",
        "returns to the same state it was in just now.",
      ].join("\n"),
    );
    if (!ok) return;
    archiveMut.mutate(id, {
      onSuccess: (summary) => {
        toast.success(
          `Exhibition archived (${summary.archivedCount} children cascaded).`,
        );
      },
      onError: (err) =>
        toast.error(
          err instanceof ApiError && err.message
            ? err.message
            : "Archive failed",
        ),
    });
  };

  const handleRestore = () => {
    if (!id || !existing) return;
    const snapshot = existing.archivedStateSnapshot ?? {};
    const snapSize = Object.keys(snapshot).length;
    const publishedInSnap = Object.values(snapshot).filter(
      (s) => s === "published",
    ).length;
    const ok = window.confirm(
      [
        `Restore ${existing.titleTc || existing.code}?`,
        "",
        "This will bring back:",
        "  • the exhibition (re-published after its publish gate runs)",
        `  • ${snapSize} children (${publishedInSnap} will re-attempt publish)`,
        "",
        "Anything that fails its publish gate (e.g., title_tc /",
        "description_tc went empty) stays at draft and you'll see the",
        "list in the next toast.",
      ].join("\n"),
    );
    if (!ok) return;
    restoreMut.mutate(id, {
      onSuccess: (summary) => {
        if (summary.partialFailures.length > 0) {
          toast.warning(
            `Restored, but ${summary.partialFailures.length} children stayed at draft (publish gate failed). See audit log.`,
          );
        } else {
          toast.success(
            `Restored. ${summary.restoredCount} children re-published.`,
          );
        }
      },
      onError: (err) =>
        toast.error(
          err instanceof ApiError && err.message
            ? err.message
            : "Restore failed",
        ),
    });
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    if (!id || !existing) return;
    setExporting(true);
    try {
      const { blob, filename } = await exportExhibition(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${filename}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message ? err.message : "Export failed",
      );
    } finally {
      setExporting(false);
    }
  };

  const handlePurge = () => {
    if (!id || !existing) return;
    const reason = window.prompt(
      [
        `Purge ${existing.titleTc || existing.code}?`,
        "",
        "This will IRREVERSIBLY:",
        "  • hard-delete every cover image's bytes from storage",
        "  • mark the exhibition + zones + exhibits as purged (rows kept as tombstones)",
        "",
        "Visitor stats stay queryable. The export package, if any, is",
        "outside the system and unaffected.",
        "",
        "Type a reason (1-500 chars). Cancel to abort.",
      ].join("\n"),
      "",
    );
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    const confirmed = window.confirm(
      `Final confirmation: purge ${existing.titleTc || existing.code}? This cannot be undone from the UI.`,
    );
    if (!confirmed) return;
    purgeMut.mutate(
      { exhibitionId: id, reason: reason.trim() },
      {
        onSuccess: (summary) => {
          toast.success(
            `Exhibition purged (${summary.archivedCount} assets removed; rows tombstoned).`,
          );
        },
        onError: (err) =>
          toast.error(
            err instanceof ApiError && err.message
              ? err.message
              : "Purge failed",
          ),
      },
    );
  };

  const handleDelete = () => {
    if (!id) return;
    if (!window.confirm("Soft-delete this exhibition?")) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        toast.success("Exhibition deleted");
        navigate("/staff/exhibitions");
      },
      onError: (err) =>
        toast.error(
          err instanceof ApiError && err.message
            ? err.message
            : "Delete failed",
        ),
    });
  };

  if (isEdit && loadingExisting) {
    return (
      <div className="text-center py-12" data-testid="loading-state">
        Loading...
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="exhibition-form-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate("/staff/exhibitions")}
            variant="ghost"
            size="sm"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-heading font-bold">
            {isEdit
              ? `Edit exhibition${existing ? ` (${existing.code})` : ""}`
              : "Create exhibition"}
          </h1>
          {isEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => navigate(`/staff/exhibition/${id}/qr`)}
              data-testid="view-qr-board-button"
            >
              QR codes
            </Button>
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-4xl mx-auto px-6 py-8 space-y-8"
        data-testid="exhibition-form"
      >
        {isLocked && (
          <LockdownBanner
            isPurged={isPurged}
            purged={
              <>
                This exhibition has been <strong>purged</strong>. Its bytes are
                gone; this row is a tombstone for audit + statistics continuity.
                Read-only.
              </>
            }
            archived={
              <>
                This exhibition is <strong>archived</strong> — fields are
                read-only. Click <em>Restore</em> in the Lifecycle section below
                to bring it back before editing.
              </>
            }
          />
        )}
        {!isLocked && (
          <ImageUpload
            label="Cover image"
            previewUrl={imagePreview}
            onFileSelected={handleFileSelected}
            onFileTooLarge={handleFileTooLarge}
            onRemove={
              isEdit && existing?.coverImageId && !imageFile
                ? () => setRemoveCover(true)
                : undefined
            }
            removalPending={removeCover}
            onUndoRemove={() => setRemoveCover(false)}
          />
        )}
        {isLocked && imagePreview && (
          <div className="space-y-2" data-testid="cover-image-readonly">
            <Label>Cover image</Label>
            <img
              src={imagePreview}
              alt="Cover"
              className="w-full max-w-md h-64 object-cover rounded-lg border border-border"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <p className="text-xs text-muted-foreground">
              Read-only while {isPurged ? "purged" : "archived"}.
            </p>
          </div>
        )}
        <LocalizedContentTabs
          titles={{ tc: form.titleTc, en: form.titleEn, sc: form.titleSc }}
          descriptions={{
            tc: form.descriptionTc,
            en: form.descriptionEn,
            sc: form.descriptionSc,
          }}
          onTitleChange={(lang, value) =>
            setForm((f) => ({ ...f, [TITLE_FIELD[lang]]: value }))
          }
          onDescriptionChange={(lang, value) =>
            setForm((f) => ({ ...f, [DESC_FIELD[lang]]: value }))
          }
          disabled={isLocked}
        />

        <fieldset className="p-6 bg-white border border-border rounded-lg space-y-3">
          <legend className="px-2 font-heading font-semibold">
            Ownership (controls who can edit Zones / Exhibits inside)
          </legend>
          <div className="space-y-2">
            <Label>Owner team</Label>
            <select
              value={form.ownerTeamId}
              onChange={setOwnerTeam}
              disabled={isLocked}
              className="w-full border rounded px-3 py-2 disabled:bg-muted disabled:text-muted-foreground"
              data-testid="owner-team-select"
            >
              <option value="">— none —</option>
              {scopedTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Owner department</Label>
            <select
              value={form.ownerDepartmentId}
              onChange={setOwnerDept}
              disabled={isLocked}
              className="w-full border rounded px-3 py-2 disabled:bg-muted disabled:text-muted-foreground"
              data-testid="owner-department-select"
            >
              <option value="">— none —</option>
              {scopedDepartments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            Independent grants — a user passing either check has access.
          </p>
        </fieldset>

        {isEdit && existing && (
          <>
            <fieldset className="p-6 bg-white border border-border rounded-lg space-y-3">
              <legend className="px-2 font-heading font-semibold">
                Lifecycle
              </legend>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm" data-testid="lifecycle-state-display">
                  Current state:{" "}
                  <strong>
                    {existing.state === "published"
                      ? "▣ published"
                      : existing.state === "archived"
                        ? "▼ archived"
                        : existing.state === "purged"
                          ? "✕ purged"
                          : "◌ draft"}
                  </strong>
                </span>
                {existing.state !== "archived" && (
                  <Button
                    type="button"
                    onClick={handlePublish}
                    variant="outline"
                    size="sm"
                    data-testid="publish-toggle-button"
                  >
                    {existing.state === "published" ? "Unpublish" : "Publish"}
                  </Button>
                )}
                {existing.state === "published" && (
                  <Button
                    type="button"
                    onClick={handleArchive}
                    variant="outline"
                    size="sm"
                    data-testid="archive-button"
                  >
                    Archive…
                  </Button>
                )}
                {existing.state === "archived" && (
                  <>
                    <Button
                      type="button"
                      onClick={handleRestore}
                      variant="outline"
                      size="sm"
                      data-testid="restore-button"
                    >
                      Restore exhibition
                    </Button>
                    <Button
                      type="button"
                      onClick={handleExport}
                      variant="outline"
                      size="sm"
                      disabled={exporting}
                      data-testid="export-button"
                    >
                      {exporting ? "Exporting…" : "Export package…"}
                    </Button>
                    <Button
                      type="button"
                      onClick={handlePurge}
                      variant="destructive"
                      size="sm"
                      data-testid="purge-button"
                    >
                      Purge…
                    </Button>
                  </>
                )}
              </div>
              {existing.state === "archived" && (
                <p className="text-xs text-muted-foreground">
                  This exhibition (and its zones / exhibits) is hidden from
                  visitors. <strong>Restore</strong> brings everything back to
                  its pre-archive state where the publish gate allows it.{" "}
                  <strong>Purge</strong> permanently removes the asset bytes and
                  tombstones the rows (stats stay queryable).
                </p>
              )}
            </fieldset>

            <fieldset
              className="p-6 bg-white border border-border rounded-lg space-y-3"
              data-testid="zones-in-exhibition-fieldset"
            >
              <legend className="px-2 font-heading font-semibold">
                Placement &amp; Linkage
              </legend>
              <CodeSection
                code={existing.code}
                label="Exhibition"
                entityNoun="exhibition"
                onRegenerate={handleRegenCode}
                regenerateDisabledReason={
                  isLocked ? "Locked while archived or purged." : null
                }
              />
              <h4 className="font-medium text-sm">
                Zones in this exhibition ({zonesInExhibition.length})
              </h4>
              {!isLocked && zonesInExhibition.length > 0 && (
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBatchOpen(true)}
                    data-testid="batch-upload-button"
                  >
                    Batch upload exhibits
                  </Button>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Create many exhibits at once from an Excel workbook — one
                    tab per zone.
                  </p>
                </div>
              )}
              {zonesInExhibition.length === 0 ? (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="zones-in-exhibition-empty"
                >
                  No zones in this exhibition yet. Open an existing zone and set
                  its Parent Exhibition to this one, or create a new zone from
                  the Zones page.
                </p>
              ) : (
                <table
                  className="w-full text-sm"
                  data-testid="zones-in-exhibition-table"
                >
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1 font-medium">#</th>
                      {!isLocked && (
                        <th className="px-2 py-1 font-medium">Order</th>
                      )}
                      <th className="px-2 py-1 font-medium">Exhibit Code</th>
                      <th className="px-2 py-1 font-medium">Title (TC)</th>
                      <th className="px-2 py-1 font-medium">Title (EN)</th>
                      <th className="px-2 py-1 font-medium">Exhibits</th>
                      <th className="px-2 py-1 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {zonesInExhibition.map((z, pos) => (
                      <tr
                        key={z.id}
                        onClick={() => navigate(`/staff/zone/${z.id}/edit`)}
                        className="cursor-pointer hover:bg-muted/30"
                        data-testid={`zones-in-exhibition-row-${z.id}`}
                      >
                        <td className="px-2 py-1 text-muted-foreground tabular-nums">
                          {pos + 1}
                        </td>
                        {!isLocked && (
                          <td
                            className="px-1 py-0.5 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={pos === 0 || reorderZones.isPending}
                              onClick={() => moveZone(z.id, -1)}
                              aria-label={`Move ${z.titleTc || z.code} up`}
                              data-testid={`zone-move-up-${z.id}`}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={
                                pos === zonesInExhibition.length - 1 ||
                                reorderZones.isPending
                              }
                              onClick={() => moveZone(z.id, 1)}
                              aria-label={`Move ${z.titleTc || z.code} down`}
                              data-testid={`zone-move-down-${z.id}`}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                          </td>
                        )}
                        <td className="px-2 py-1 font-mono">{z.code}</td>
                        <td className="px-2 py-1">{z.titleTc}</td>
                        <td className="px-2 py-1 text-muted-foreground">
                          {z.titleEn || "—"}
                        </td>
                        <td className="px-2 py-1">
                          {z.exhibitIds?.length ?? 0}
                        </td>
                        <td className="px-2 py-1">
                          {z.state === "published"
                            ? "▣ published"
                            : z.state === "archived"
                              ? "▼ archived"
                              : z.state === "purged"
                                ? "✕ purged"
                                : "◌ draft"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-xs text-muted-foreground">
                Rows are in visitor order — the arrows reorder and save
                immediately. Click a zone to edit it. Reassigning a zone away
                from this exhibition happens in the zone&apos;s own form via the
                Parent Exhibition dropdown.
              </p>
            </fieldset>

            <fieldset className="p-6 bg-white border border-red-300 rounded-lg space-y-3">
              <legend className="px-2 font-heading font-semibold text-red-700">
                Danger zone
              </legend>
              <Button
                type="button"
                onClick={handleDelete}
                variant="destructive"
                size="sm"
                disabled={isLocked}
                data-testid="delete-button"
              >
                Soft-delete exhibition
              </Button>
              <p className="text-xs text-muted-foreground">
                Blocked if any zone is still attached. Reassign or delete zones
                first.
                {isLocked && (
                  <>
                    {" "}
                    {existing.state === "archived"
                      ? "Restore the exhibition before soft-deleting."
                      : "Soft-delete is not meaningful on a purged tombstone."}
                  </>
                )}
              </p>
            </fieldset>
          </>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/staff/exhibitions")}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLocked || (isEdit && !dirty)}
            title={isEdit && !dirty ? "No changes to save" : undefined}
            data-testid="save-button"
          >
            Save
          </Button>
        </div>
      </form>
      {isEdit && id && existing && (
        <BatchUploadDialog
          exhibitionId={id}
          exhibitionCode={existing.code}
          open={batchOpen}
          onOpenChange={setBatchOpen}
        />
      )}
    </div>
  );
};

export default ExhibitionForm;
